import { useFieldErrors } from '../hooks/useFieldErrors';
import { Flag } from '../components/shared/Flag';
/**
 * AcceptInvitePage.tsx — F0.10
 *
 * El backend ya sabía canjear invitaciones (`POST /api/invitations/:token/accept`)
 * pero no había pantalla: el invitado abría el link, veía un banner en el login
 * y ningún formulario donde escribir sus datos. Esta es esa pantalla.
 *
 * Ruta pública: `/accept-invite/:token` (sin sesión, sin tenant en la URL — el
 * tenant lo dice la propia invitación).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import {
  Text, Input, Button, Spinner, MessageBar, MessageBarBody,
  Field, makeStyles, tokens,
} from '@fluentui/react-components';
import { PersonAdd24Regular } from '@fluentui/react-icons';
import { getInvitation, acceptInvitation, type InvitationInfo } from '../api/invitations.api';
import { getTenantConfig } from '../api/tenant.api';
import { useAuthStore } from '../store/auth.store';
import { useTenantStore } from '../store/tenant.store';
import { useT, LOCALE_OPTIONS } from '../i18n';
import { usePageTitle } from '../hooks/usePageTitle';
import { useLocaleStore, type Locale } from '../store/locale.store';
import { telegramInviteDeepLink } from '../utils/telegram-deeplink';
import { isValidNewPassword, isValidPhoneE164, normalizePhoneInput } from '../utils/validators';
import { PhoneInput } from '../components/shared/PhoneInput';
import { GoogleSignInButton } from '../components/auth/GoogleSignInButton';
import { NewPasswordFields } from '../components/shared/PasswordField';
import type { AuthUser } from '../types/api.types';

const useStyles = makeStyles({
  root: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colorNeutralBackground2,
    padding: '16px',
  },
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: '40px',
    width: '100%',
    maxWidth: '420px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: tokens.shadow16,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    textAlign: 'center',
  },
  roles: {
    color: tokens.colorBrandForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  hint: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
  },
});

/** Mismo criterio laxo que el backend: basta con que sea direccionable. */
function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

export function AcceptInvitePage() {
  const s = useStyles();
  const t = useT();
  usePageTitle(t.pageTitle.acceptInvite);
  const f = t.auth;
  const { locale, setLocale } = useLocaleStore();
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();

  const setSession = useAuthStore(st => st.setSession);
  const setTenant = useTenantStore(st => st.setTenant);

  const [minLen, setMinLen] = useState<number | undefined>(undefined);
  const [invite, setInvite] = useState<InvitationInfo | null>(null);
  const [checking, setChecking] = useState(true);
  const [inviteError, setInviteError] = useState<string | null>(null);

  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [phoneTaken, setPhoneTaken] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // ── Validar el token antes de mostrar el formulario ──────────────────────
  useEffect(() => {
    if (!token) {
      setInviteError(f.acceptInviteInvalid);
      setChecking(false);
      return;
    }

    let cancelled = false;
    getInvitation(token)
      .then(info => {
        if (cancelled) return;
        setInvite(info);
        // Largo mínimo de la empresa que invita; si falla se queda el default (8).
        if (info.tenantId) getTenantConfig(info.tenantId).then(c => { if (!cancelled) setMinLen(c.passwordMinLength); }).catch(() => undefined);
        // El ADMIN ya escribió el correo al invitar: se precarga y el invitado
        // solo lo confirma (es la llave de su login con Google).
        if (info.email) setEmail(info.email);
      })
      .catch(err => {
        if (cancelled) return;
        setInviteError(err?.response?.status === 410 ? f.acceptInviteUsed : f.acceptInviteInvalid);
      })
      .finally(() => { if (!cancelled) setChecking(false); });

    return () => { cancelled = true; };
  }, [token, f.acceptInviteInvalid, f.acceptInviteUsed]);

  const fe = useFieldErrors();

  // El teléfono es opcional, pero si se escribe debe ser válido; sin Google hace falta teléfono o correo,
  // y la contraseña entra por correo.
  const hasPhone = phoneNumber.trim().length > 0;
  function validate(withGoogle: boolean): boolean {
    return fe.validate({
      name: !name.trim() && f.acceptInviteNameRequired,
      phone: !withGoogle && hasPhone && !isValidPhoneE164(phoneNumber) && f.acceptInvitePhoneRequired,
      email: !withGoogle && !!email.trim() && !isValidEmail(email) && f.acceptInviteEmailInvalid,
      password: !withGoogle && wantsPassword && (
        (!email.trim() && f.acceptInvitePasswordNeedsEmail)
        || (!isValidNewPassword(password, confirmPassword, minLen) && f.acceptInvitePasswordShort)
      ),
      identity: !withGoogle && !hasPhone && !email.trim() && f.acceptInviteIdentityRequired,
    });
  }

  // La contraseña es opcional, pero si se escribe debe ser fuerte y confirmarse.
  const wantsPassword = password.length > 0 || confirmPassword.length > 0;

  const canSubmit = !wantsPassword || isValidNewPassword(password, confirmPassword, minLen);

  async function handleSubmit(idToken?: string) {
    setFormError(null);
    if (!validate(!!idToken)) return;
    if (!token) return;

    setSubmitting(true);
    setFormError(null);
    try {
      const res = await acceptInvitation(token, idToken
        // Con Google el correo lo da Google; nada más que el nombre hace falta.
        ? { name: name.trim(), idToken }
        : {
          name: name.trim(),
          phoneNumber: hasPhone ? normalizePhoneInput(phoneNumber) : undefined,
          email: email.trim() || undefined,
          password: password || undefined,
        });

      // Si la empresa exige OTP el backend ya envió el código por Telegram y no emite
      // sesión: se completa el ingreso en /login (mismo camino que cualquier login).
      if (res.requiresOtp || !res.token) {
        navigate('/login', { replace: true });
        return;
      }

      const user = res.user as AuthUser;
      setSession(res.token, user);
      const destCode = user.tenantId ?? invite?.tenantId ?? 'demo';
      setTenant(destCode, destCode);
      navigate(res.needsRoleSelection ? `/${destCode}/select-role` : `/${destCode}/dashboard`, { replace: true });
    } catch (err: any) {
      const status = err?.response?.status;
      const serverMsg: string | undefined = err?.response?.data?.error;
      if (status === 401) setFormError(t.register.googleFailed);
      else if (status === 409 && hasPhone && !idToken && !/correo|email|邮箱/i.test(serverMsg ?? '')) {
        setFormError(f.acceptInvitePhoneTaken); setPhoneTaken(true);
      } else if (status === 409 && serverMsg) setFormError(serverMsg);
      else if (status === 410 || status === 404) setInviteError(f.acceptInviteInvalid);
      else setFormError(f.acceptInviteError);
    } finally {
      setSubmitting(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  if (checking) {
    return (
      <div className={s.root}>
        <div className={s.card}>
          <Spinner label={f.acceptInviteLoading} />
        </div>
      </div>
    );
  }

  if (inviteError || !invite) {
    return (
      <div className={s.root}>
        <div className={s.card}>
          <MessageBar intent="error">
            <MessageBarBody>{inviteError ?? f.acceptInviteInvalid}</MessageBarBody>
          </MessageBar>
          <Button appearance="primary" onClick={() => navigate('/login', { replace: true })}>
            {f.acceptInviteGoToLogin}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.root}>
      <div className={s.card}>
        <div className={s.header}>
          <PersonAdd24Regular style={{ color: tokens.colorBrandForeground1 }} />
          <Text size={600} weight="semibold">{f.acceptInviteTitle}</Text>
          <Text size={300}>
            {f.acceptInviteSubtitle}{' '}
            <span className={s.roles}>{invite.roles.join(', ')}</span>
          </Text>
        </div>

        {formError && (
          <MessageBar intent="error">
            <MessageBarBody>{formError}</MessageBarBody>
          </MessageBar>
        )}

        {/* Teléfono ya registrado: la invitación sirve para VINCULAR Telegram a esa
            cuenta (el bot lo hace con /start invite_<token>), no para darla de alta. */}
        {phoneTaken && token && telegramInviteDeepLink(token) && (
          <Button appearance="primary" as="a" href={telegramInviteDeepLink(token)!}
            target="_blank" rel="noopener noreferrer">
            {f.acceptInviteLinkTelegram}
          </Button>
        )}

        <Field label={f.acceptInviteName} required {...fe.props('name')}>
          <Input value={name} onChange={(_, d) => { setName(d.value); fe.clear('name'); }} maxLength={100} />
        </Field>

        <Field label={f.acceptInvitePhoneOptional}>
          <PhoneInput
            value={phoneNumber}
            error={fe.errors.phone}
            onChange={(v) => { setPhoneNumber(v); fe.clear('phone'); }}
            name="agrovid-invite-phone"
            autoComplete="off"
            ariaLabel={f.acceptInvitePhone}
          />
        </Field>

        <Field label={f.acceptInviteEmail} {...fe.props('email')}>
          <Input value={email} onChange={(_, d) => { setEmail(d.value); fe.clear('email'); }} type="email" />
        </Field>

        <NewPasswordFields
          minLength={minLen}
          password={password}
          confirm={confirmPassword}
          onPasswordChange={(v) => { setPassword(v); fe.clear('password'); }}
          onConfirmChange={(v) => { setConfirmPassword(v); fe.clear('password'); }}
          passwordLabel={f.acceptInvitePassword}
          disabled={submitting}
          wrap={(kind, input) => (
            <Field label={kind === 'password' ? f.acceptInvitePassword : t.passwordField.confirmLabel} {...(kind === 'confirm' ? fe.props('password') : {})}>
              {input}
            </Field>
          )}
        />
        <Text className={s.hint}>{f.acceptInvitePasswordHint}</Text>

        {fe.errors.identity && (
          <MessageBar intent="error" role="alert">
            <MessageBarBody>{fe.errors.identity}</MessageBarBody>
          </MessageBar>
        )}

        <Button appearance="primary" onClick={() => void handleSubmit()} disabled={submitting || !canSubmit}>
          {submitting ? <Spinner size="tiny" /> : f.acceptInviteSubmit}
        </Button>

        {/* Entrar con Google: sin teléfono ni Telegram (el correo lo da Google). */}
        <Text className={s.hint} style={{ textAlign: 'center' }}>{t.register.methodOr}</Text>
        <GoogleSignInButton
          locale={locale}
          onCredential={(r) => {
            if (!r.credential) { setFormError(t.register.googleFailed); return; }
            void handleSubmit(r.credential);
          }}
        />

        {/* Selector de idioma — el invitado llega por link, sin sesión, y no tiene
            otro lugar donde elegir es/en/zh antes de llenar el formulario. */}
        <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
          {LOCALE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setLocale(opt.value as Locale)}
              aria-label={opt.label}
              aria-pressed={locale === opt.value}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '4px 10px', borderRadius: '999px', cursor: 'pointer',
                fontSize: '12px', fontWeight: locale === opt.value ? 600 : 400,
                border: `1px solid ${locale === opt.value ? tokens.colorBrandStroke1 : tokens.colorNeutralStroke2}`,
                background: locale === opt.value ? tokens.colorBrandBackground2 : 'transparent',
                color: locale === opt.value ? tokens.colorBrandForeground1 : tokens.colorNeutralForeground3,
              }}
            >
              <Flag iso={opt.flagIso} />
              <span>{opt.value.toUpperCase()}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
